import React, { FC, useState } from 'react';
import Ticket, { TicketState } from 'src/interfaces/api/Ticket';
import { Container, Grid, Typography } from '@material-ui/core';
import { createUseStyles } from 'src/theming';
import TicketNavigation from './components/TicketNavigation';
import Card from 'src/components/Card';
import GeneralInformationsCard from './components/GeneralInformationsCard';
import Button from 'src/components/Button';
import { MdRefresh as RelaunchIcon } from '@react-icons/all-files/md/MdRefresh';
import TicketCloseModal from './components/TicketCloseModal';
import TicketOpenModal from './components/TicketOpenModal';
import Badge from '../../../../components/Badge';
import DunningTicketModal from './components/DunningTicketModal';

const useStyles = createUseStyles((theme) => ({
  root: {
    marginTop: 10,
  },
  container: {
    marginTop: 15,
    marginBottom: 15,
  },
  timerContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  planningAction: {
    marginTop: 20,
    justifyContent: 'center',
  },
  titleContainer: {
    justifyContent: 'space-between',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    '& > *': {
      marginRight: '10px !important',
    },
  },
  actions: {
    '& > *': {
      marginLeft: '10px !important',
    },
  },
  [theme.breakpoints.up('md')]: {
    planningAction: {
      justifyContent: 'flex-end',
    },
    timerContainer: {
      display: 'initial',
    },
  },
}));

interface Props {
  pageHash?: string;
  ticket: Ticket;
  ticketState?: TicketState;
  onChangePageHash: (newHash: string) => void;
  onCloseTicket: (id: string, ticketClosedReasonId: number, comment?: string) => Promise<Ticket>;
  onOpenTicket: (id: string) => Promise<Ticket>;
  onDunningTicket: (comment?: string) => Promise<Ticket>;
}

const TicketView: FC<Props> = ({
  onChangePageHash,
  pageHash,
  ticket,
  ticketState,
  onDunningTicket,
  onOpenTicket,
  onCloseTicket,
}) => {
  const [isTicketCloseModalOpen, setIsTicketCloseModalOpen] = useState(false);
  const [isTicketOpenModalOpen, setIsTicketOpenModalOpen] = useState(false);
  const [isDunningModalOpen, setIsDunningModalOpen] = useState(false);
  const classes = useStyles();

  const handleClickCloseTicket = () => {
    if (ticket.closingDate) {
      setIsTicketOpenModalOpen(true);
    } else {
      setIsTicketCloseModalOpen(true);
    }
  };

  return (
    <>
      <Container className={classes.root} maxWidth='xl'>
        <Grid container justifyContent='center' spacing={3}>
          <Grid item xs={12}>
            <Grid container className={classes.titleContainer} spacing={3}>
              <Grid item xs={12} sm='auto' className={classes.title}>
                <Typography variant='h5'>
                  <strong>Ticket</strong> #{ticket.id}
                </Typography>
                <Badge
                  color={ticket.closingDate ? 'error' : 'info'}
                  text={ticket.closingDate ? 'Clos' : ticketState?.name || ''}
                />
              </Grid>

              <Grid item xs={12} sm='auto' className={classes.actions}>
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={() => setIsDunningModalOpen(true)}
                  startIcon={<RelaunchIcon />}
                >
                  Relancer
                </Button>

                <Button variant='contained' color='primary' onClick={handleClickCloseTicket}>
                  {ticket.closingDate ? 'Réouvrir le ticket' : 'Clore le ticket'}
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <Grid item md={3} xs={12}>
            <GeneralInformationsCard ticket={ticket} />
          </Grid>

          <Grid item md={9} xs={12}>
            <Card>
              <TicketNavigation onChangePageHash={onChangePageHash} pageHash={pageHash} ticket={ticket} />
            </Card>
          </Grid>
        </Grid>
      </Container>

      <TicketCloseModal
        ticket={ticket}
        open={isTicketCloseModalOpen}
        onCloseTicket={onCloseTicket}
        onClose={() => setIsTicketCloseModalOpen(false)}
      />

      <TicketOpenModal
        ticket={ticket}
        open={isTicketOpenModalOpen}
        onOpenTicket={onOpenTicket}
        onClose={() => setIsTicketOpenModalOpen(false)}
      />

      <DunningTicketModal
        open={isDunningModalOpen}
        onDunningTicket={onDunningTicket}
        onClose={() => setIsDunningModalOpen(false)}
      />
    </>
  );
};

export default TicketView;
